import React, { useEffect, useState } from 'react';
import ShowcaseReel from './ShowcaseReel';
import { triggerLightbox } from '../layout-system/ImageLightbox';
import { useTheme } from '../theme-system/ThemeContext';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import PhoneStack from './PhoneStack';

const SITE_URL = 'https://pudeuc.com';
const NAVY = '#1e3150';

const img = (name) => `/pudeuc-brand/${name}.webp`;

/* Every plate on the page, in reading order, so the lightbox can walk them. */
const PLATES = [
  'pudeuc-logo-navy-background',
  'pudeuc-16', 'pudeuc-17',
  'pudeuc-22', 'pudeuc-19', 'social-share',
  'pudeuc-4', 'pudeuc-5', 'pudeuc-6', 'pudeuc-9', 'pudeuc-2', 'pudeuc-10', 'pudeuc-3',
  'pudeuc-11', 'pudeuc-14', 'pudeuc-18', 'pudeuc-8', 'pudeuc-15',
  'pudeuc-21', 'pudeuc-13', 'pudeuc-12', 'pudeuc-20'
].map(img);

/* -------------------------------------------------------------- helpers -- */

const useIsMobile = (bp = 768) => {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < bp : false
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < bp);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bp]);
  return mobile;
};

/* Brand navy is unreadable as text on the dark theme, which is the default —
   so the accent lightens instead of staying fixed. */
const useAccent = () => {
  const { isDark } = useTheme();
  return isDark ? '#9db2d4' : NAVY;
};

const FullBleed = ({ children }) => (
  <div style={{
    marginLeft: 'calc(-1 * clamp(20px, 4vw, 40px))',
    marginRight: 'calc(-1 * clamp(20px, 4vw, 40px))',
    width: 'calc(100% + 2 * clamp(20px, 4vw, 40px))'
  }}>
    {children}
  </div>
);

const Eyebrow = ({ children, color }) => (
  <span style={{
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    color: color || 'var(--text-secondary)'
  }}>
    {children}
  </span>
);

/* One plate: true aspect ratio so nothing is cropped, caption underneath. */
const Plate = ({ name, span, ar, caption }) => {
  const [hover, setHover] = useState(false);
  const mobile = useIsMobile();
  const src = img(name);

  return (
    <figure style={{
      margin: 0,
      gridColumn: `span ${mobile ? (span >= 4 ? 2 : 1) : span}`
    }}>
      <div
        onClick={() => triggerLightbox(src, PLATES)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          aspectRatio: ar,
          overflow: 'hidden',
          borderRadius: '8px',
          border: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
          cursor: 'zoom-in',
          transform: hover ? 'translateY(-3px)' : 'none',
          boxShadow: hover ? '0 14px 34px rgba(0,0,0,0.22)' : '0 1px 6px rgba(0,0,0,0.06)',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease'
        }}
      >
        <img
          src={src}
          alt={caption || ''}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    </figure>
  );
};

/* Plates side by side at one shared height: each takes width in proportion
   to its aspect ratio, so a wide and a tall frame line up top and bottom. */
const PlateRow = ({ plates }) => (
  <div style={{ display: 'flex', gap: 'clamp(12px, 1.6vw, 18px)' }}>
    {plates.map((p) => {
      const src = img(p.name);
      return (
        <div key={p.name} onClick={() => triggerLightbox(src, PLATES)} style={{
          flex: `${p.ar} 1 0`, aspectRatio: String(p.ar), overflow: 'hidden', borderRadius: '8px',
          border: '1px solid var(--border-color)', cursor: 'zoom-in'
        }}>
          <img src={src} alt={p.caption} loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      );
    })}
  </div>
);

const Mosaic = ({ plates }) => {
  const mobile = useIsMobile();
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)',
      gap: mobile ? '12px' : '18px',
      alignItems: 'start'
    }}>
      {plates.map((p) => <Plate key={p.name} {...p} />)}
    </div>
  );
};

/* ---------------------------------------------------------------- store -- */

/* Full-page captures of pudeuc.com, re-taken 2026-10-19 (live prices: cap 179 SR). Each sits in a phone frame
   that scrolls on its own, so the whole page reads without leaving this one. */
const SITE_PAGES = [
  { file: 'home', label: 'Home', path: '/' },
  { file: 'shop', label: 'Shop', path: '/collections/all' },
  { file: 'tee-white', label: 'Tee — White', path: '/products/pudeuc-tee-white' },
  { file: 'tee-navy', label: 'Tee — Navy', path: '/products/pudeuc-tee-navy' },
  { file: 'tee-black', label: 'Tee — Black', path: '/products/pudeuc-tee-black' },
  { file: 'jeans', label: 'Jeans — Classic Fit', path: '/products/pudeuc-jeans-classic-fit' },
  { file: 'cap-navy', label: 'Cap — Navy', path: '/products/pudeuc-cap-navy' }
];

const StoreFrames = () => <PhoneStack pages={SITE_PAGES} baseUrl={SITE_URL} imgBase="/pudeuc-brand/site/" />;

const shuffled = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* The collection as a deck of cards: drag or swipe the top one left or right
   and it spins away to the back of the pile. Photos from pudeuc.com. */
const DECK = [
  { file: 'm-navy-tee-01', name: 'Tee — Navy' },
  { file: 'g-1', name: 'The letter' },
  { file: 'm-white-tee-01', name: 'Tee — White' },
  { file: 'm-navy-cap-03', name: 'Cap — Navy, at sea' },
  { file: 'm-black-tee-01', name: 'Tee — Black' },
  { file: 'g-5', name: 'The rose' },
  { file: 'm-navy-tee-02', name: 'Tee — Navy' },
  { file: 'c-pudeuc-cap-beige-0', name: 'Cap — Beige' },
  { file: 'm-white-tee-05', name: 'Tee — White' },
  { file: 'c-pudeuc-cap-black-1', name: 'Cap — Black' },
  { file: 'm-navy-tee-03', name: 'Tee — Navy' },
  { file: 'g-4', name: 'Tee — White, with the cap' },
  { file: 'm-black-tee-04', name: 'Tee — Black' },
  { file: 'c-pudeuc-cap-navy-1', name: 'Cap — Navy' },
  { file: 'm-white-tee-02', name: 'Tee — White' },
  { file: 'm-navy-tee-04', name: 'Tee — Navy, cap on' },
  { file: 'c-pudeuc-cap-navy-2', name: 'Cap — Navy' },
  { file: 'g-7', name: 'The box' },
  { file: 'c-pudeuc-cap-navy-3', name: 'The box, opened' }
];

const SwipeCard = ({ item, depth, onThrow }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-260, 260], [-18, 18]);
  const top = depth === 0;
  const fan = [0, 7, -7, 13, -13][depth] ?? 0;
  const shift = [0, 26, -26, 46, -46][depth] ?? 0;
  return (
    <motion.div
      drag={top ? 'x' : false}
      dragSnapToOrigin
      dragElastic={0.9}
      onDragEnd={(e, info) => {
        if (Math.abs(info.offset.x) > 90 || Math.abs(info.velocity.x) > 500) {
          onThrow(info.offset.x > 0 ? 1 : -1);
        }
      }}
      style={{
        x, rotate: top ? rotate : fan,
        position: 'absolute', inset: 0,
        zIndex: 10 - depth,
        cursor: top ? 'grab' : 'default',
        touchAction: 'pan-y'
      }}
      animate={{ scale: 1 - depth * 0.04, y: depth * 8, x: top ? 0 : shift, opacity: depth > 4 ? 0 : 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
      whileTap={top ? { cursor: 'grabbing', scale: 1.02 } : undefined}
    >
      <div style={{
        width: '100%', height: '100%', borderRadius: '22px', overflow: 'hidden',
        background: '#f4f2ee', border: '1px solid var(--border-color)',
        boxShadow: '0 24px 50px rgba(0,0,0,0.28)', position: 'relative'
      }}>
        <img src={`/pudeuc-brand/site/${item.file}.webp`} alt={item.name} draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, padding: '40px 18px 16px',
          background: 'linear-gradient(transparent, rgba(14,24,40,0.78))', color: '#fff'
        }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.24em', textTransform: 'uppercase', opacity: 0.75 }}>Pudeuc</div>
          <div style={{ fontSize: '17px', fontWeight: 600, letterSpacing: '-0.01em', marginTop: '4px' }}>{item.name}</div>
        </div>
      </div>
    </motion.div>
  );
};

const CardStack = () => {
  // shuffled after mount, not during render: the page is prerendered, and a
  // random first render would never match the static HTML it hydrates
  const [order, setOrder] = useState(() => DECK.map((_, i) => i));
  useEffect(() => { setOrder((o) => shuffled(o)); }, []);
  const [seen, setSeen] = useState(0);
  const accent = useAccent();
  const n = DECK.length;
  const next = () => { setOrder((o) => [...o.slice(1), o[0]]); setSeen((v) => (v + 1) % n); };
  const prev = () => { setOrder((o) => [o[o.length - 1], ...o.slice(0, -1)]); setSeen((v) => (v - 1 + n) % n); };
  const reshuffle = () => { setOrder((o) => shuffled(o)); setSeen(0); };
  const btn = {
    width: 46, height: 46, borderRadius: '50%', cursor: 'pointer', fontSize: 18,
    border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)'
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', padding: '24px 0 8px', overflow: 'hidden' }}>
      <div style={{ position: 'relative', width: 'min(78vw, 340px)', aspectRatio: '4 / 5' }}>
        {order.slice(0, 5).map((idx, depth) => (
          <SwipeCard key={DECK[idx].file} item={DECK[idx]} depth={depth} onThrow={(dir) => (dir > 0 ? prev() : next())} />
        )).reverse()}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button style={btn} onClick={next} aria-label="Next card">←</button>
        <span style={{ fontSize: '12px', letterSpacing: '0.18em', color: accent, minWidth: 56, textAlign: 'center' }}>
          {String(seen + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
        </span>
        <button style={btn} onClick={prev} aria-label="Previous card">→</button>
        <button style={{ ...btn, width: 'auto', padding: '0 16px', fontSize: 12.5, fontWeight: 600 }} onClick={reshuffle} aria-label="Shuffle the cards">⇄ Shuffle</button>
      </div>
    </div>
  );
};

/* The Instagram grid from the pudeuc.com home page, as it runs on the site. */
const InstaGrid = () => {
  const mobile = useIsMobile();
  const shots = Array.from({ length: 9 }, (_, i) => `/pudeuc-brand/site/ig-${i + 1}.webp`);
  const tiles = Array.from({ length: 9 }, (_, i) => `/pudeuc-brand/site/g-${i + 1}.webp`);
  return (
    <div>
      {/* the 3x3 grid that runs on the product pages */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px',
        maxWidth: '720px', margin: `0 auto ${mobile ? '28px' : '44px'}`, borderRadius: '10px', overflow: 'hidden'
      }}>
        {tiles.map((src) => (
          <img key={src} src={src} alt="" loading="lazy" onClick={() => triggerLightbox(src, tiles)}
            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }} />
        ))}
      </div>
      <div style={{ columnCount: mobile ? 2 : 3, columnGap: mobile ? '10px' : '14px' }}>
        {shots.map((src) => (
          <img key={src} src={src} alt="" loading="lazy" onClick={() => triggerLightbox(src, shots)}
            style={{
              width: '100%', display: 'block', marginBottom: mobile ? '10px' : '14px',
              borderRadius: '10px', breakInside: 'avoid', cursor: 'zoom-in'
            }} />
        ))}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------- hero -- */

const HeroButton = ({ href, children, primary }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: primary
          ? (hover ? 'var(--text-secondary)' : 'var(--text-primary)')
          : (hover ? 'var(--bg-accent)' : 'var(--bg-secondary)'),
        color: primary ? 'var(--bg-primary)' : 'var(--text-primary)',
        border: primary ? '1px solid transparent' : '1px solid var(--border-color)',
        padding: '13px 26px',
        borderRadius: '10px',
        textDecoration: 'none',
        fontWeight: '600',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        transform: hover ? 'translateY(-2px)' : 'none',
        transition: 'all 0.2s ease'
      }}
    >
      {children}
    </a>
  );
};

const MetaItem = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{
      fontSize: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      color: 'var(--text-secondary)',
      marginBottom: '7px'
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '13.5px',
      fontWeight: '600',
      color: 'var(--text-primary)',
      lineHeight: '1.5'
    }}>
      {value}
    </div>
  </div>
);

const Hero = () => {
  const accent = useAccent();
  return (
    <div>
      <Eyebrow color={accent}>Fashion</Eyebrow>

      <h1 style={{
        fontSize: 'clamp(2.3rem, 5.5vw, 3.6rem)',
        fontWeight: '700',
        letterSpacing: '-0.02em',
        color: 'var(--text-primary)',
        margin: '16px 0 20px',
        lineHeight: '1.1'
      }}>
        Pudeuc
      </h1>

      <p style={{
        maxWidth: '640px',
        fontSize: 'clamp(1.02rem, 1.7vw, 1.2rem)',
        lineHeight: '1.7',
        color: 'var(--text-secondary)',
        margin: '0 0 30px'
      }}>
        Inspired by life.
      </p>

      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '38px' }}>
        <HeroButton href={SITE_URL} primary>Visit pudeuc.com →</HeroButton>
        <HeroButton href={SITE_URL}>See everything</HeroButton>
      </div>

      <div style={{
        // flex, not grid: responsive.css forces every inline "fr" grid to one column on phones
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'clamp(8px, 3vw, 30px)',
        paddingTop: '26px',
        borderTop: '1px solid var(--border-color)'
      }}>
        <MetaItem label="Role" value="Art direction" />
        <MetaItem label="Category" value="Fashion" />
        <MetaItem label="Storefront" value="Shopify" />
        <MetaItem label="Status" value="Live" />
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- banner -- */

const NavyBanner = () => (
  <FullBleed>
    <div
      onClick={() => triggerLightbox(img('pudeuc-logo-navy-background'), PLATES)}
      style={{
        cursor: 'zoom-in',
        background: NAVY,
        aspectRatio: '1600 / 520',
        overflow: 'hidden'
      }}
    >
      <img
        src={img('pudeuc-logo-navy-background')}
        alt="The Pudeuc wordmark reversed to white on brand navy, with the coordinates set beneath it"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block'
        }}
      />
    </div>
  </FullBleed>
);

/* ================================================================ content == */

export const pudeucContent = {
  pageMaxWidth: '1200px',
  breadcrumb: { text: 'Back to Home', url: '/' },

  sections: [
    { id: 'intro-card', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pudeuc" card="https://pudeuc.com" alt="Pudeuc" /> }] },
    {
      id: 'hero',
      spacing: 'clamp(36px, 5vw, 56px)',
      blocks: [{ type: 'custom', render: () => <Hero /> }]
    },
    { id: 'showcase-motion', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pudeuc" video alt="Pudeuc" /> }] },

    {
      id: 'banner',
      spacing: 'clamp(48px, 7vw, 80px)',
      blocks: [{ type: 'custom', render: () => <NavyBanner /> }]
    },
    { id: 'showcase-mark', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pudeuc" frames={[1, 2]} alt="Pudeuc" /> }] },

    {
      id: 'mark',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <Mosaic plates={[
              { name: 'pudeuc-16', span: 3, ar: '1600 / 1598', caption: 'The wordmark on its own.' },
              { name: 'pudeuc-17', span: 3, ar: '1600 / 1594', caption: 'PUDEUC ® — the formal lockup.' }
            ]} />
          )
        }
      ]
    },

    {
      id: 'campaign',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.6vw, 18px)' }}>
              <PlateRow plates={[
                { name: 'pudeuc-22', ar: 1600 / 818, caption: 'The line carried onto water.' },
                { name: 'pudeuc-19', ar: 891 / 1600, caption: 'Black and white, no product in frame.' }
              ]} />
              <Mosaic plates={[
                { name: 'social-share', span: 6, ar: '1600 / 800', caption: 'Inspired By Life, from the golf cart.' }
              ]} />
            </div>
          )
        }
      ]
    },
    { id: 'showcase-store', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pudeuc" frames={[3, 4, 5]} alt="Pudeuc" /> }] },

    {
      id: 'store',
      title: 'The store',
      blocks: [
        { type: 'custom', render: () => <StoreFrames /> }
      ]
    },

    {
      id: 'instagram',
      blocks: [{ type: 'custom', render: () => <InstaGrid /> }]
    },
    { id: 'showcase-collection', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pudeuc" frames={[7]} alt="Pudeuc" /> }] },

    {
      id: 'collection',
      blocks: [
        { type: 'custom', render: () => <CardStack /> },
        {
          type: 'custom',
          render: () => (
            <div style={{ maxWidth: '620px', margin: 'clamp(40px, 6vw, 72px) auto clamp(24px, 4vw, 40px)', textAlign: 'center' }}>
              <Eyebrow>Where it started</Eyebrow>
              <p style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.2rem)', lineHeight: 1.7, color: 'var(--text-primary)', margin: '14px 0 0' }}>
                PUDEUC began as a small tailoring collection: shirting, overshirts and knitwear.
                Then I ran into a problem with it, and I pivoted to something more accessible.
                Tees, caps and a lifestyle line, carried by the same mark.
              </p>
            </div>
          )
        },
        {
          type: 'custom',
          render: () => (
            <Mosaic plates={[
              { name: 'pudeuc-4', span: 2, ar: '1377 / 1600', caption: 'Mandarin collar, mid-grey.' },
              { name: 'pudeuc-5', span: 2, ar: '1373 / 1600', caption: 'Grey twill and blue stripe on the rail.' },
              { name: 'pudeuc-6', span: 2, ar: '1375 / 1600', caption: 'Stripe and white, folded.' },
              { name: 'pudeuc-9', span: 3, ar: '1263 / 1600', caption: 'The rail — denim, cap, shirting, jeans.' },
              { name: 'pudeuc-2', span: 3, ar: '1261 / 1600', caption: 'Three frames from the same set.' },
              { name: 'pudeuc-10', span: 4, ar: '1600 / 1333', caption: 'Black shirt, denim jacket, white shirt.' },
              { name: 'pudeuc-3', span: 2, ar: '1026 / 1386', caption: 'Linen shirting with the fragrance vials, coordinates picked out in red.' }
            ]} />
          )
        }
      ]
    },
    { id: 'showcase-packaging', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pudeuc" frames={[8, 9]} alt="Pudeuc" /> }] },

    {
      id: 'packaging',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <Mosaic plates={[
              { name: 'pudeuc-11', span: 2, ar: '1069 / 1600', caption: 'Open — printed inner lid, cap, card.' },
              { name: 'pudeuc-14', span: 2, ar: '1065 / 1600', caption: 'Black polo, boxed.' },
              { name: 'pudeuc-18', span: 2, ar: '1082 / 1600', caption: 'Linen dust bag, ribbon tied.' },
              { name: 'pudeuc-8', span: 2, ar: '805 / 1600', caption: 'Cap and case on the rail.' },
              { name: 'pudeuc-15', span: 4, ar: '1395 / 1600', caption: 'Only few things were made to last — printed inside the lid, with the card beneath.' },
              { name: 'pudeuc-21', span: 2, ar: '1048 / 1600', caption: 'Case, linen, card.' },
              { name: 'pudeuc-13', span: 2, ar: '981 / 1600', caption: 'Navy knit, folded, with the card.' },
              { name: 'pudeuc-12', span: 2, ar: '1102 / 1350', caption: 'Cap on a quilted seat.' },
              { name: 'pudeuc-20', span: 6, ar: '1600 / 1077', caption: 'Cases on the rail, in the room it was all shot in.' }
            ]} />
          )
        }
      ]
    },

  ],

  callToAction: {
    endCard: '/showcase/pudeuc/end.webp',
    brand: { bg: "#1E3150", fg: "#F4F2EE", btnBg: "#F4F2EE", btnFg: "#1E3150" , logo: "/pudeuc-brand/pudeuc-logo-navy-background.webp", logoWidth: 150, logoRadius: 10 },
    title: 'Pudeuc',
    text: 'Inspired by life.',
    button: { text: 'Visit pudeuc.com', url: SITE_URL, style: 'primary' }
  }
};
